/**
 * Alinhado a `Domain.Enum.ICL.CultureTypeEnum` no SpectralCore (ordem dos valores numéricos).
 */
export enum CultureTypeEnum {
  Cotton = 0,
  Coffe = 1,
  SugarcaneStalk = 2,
  Maize = 3,
  Soybean = 4,
  Demo = 5,
  Soil = 6,
  Other = 7,
  Eucalyptus = 8,
  SugarcaneLeaf = 9
}

const LABELS_PT: Record<number, string> = {
  [CultureTypeEnum.Cotton]: "Algodão",
  [CultureTypeEnum.Coffe]: "Café",
  [CultureTypeEnum.SugarcaneStalk]: "Cana-de-açúcar (Colmo)",
  [CultureTypeEnum.Maize]: "Milho",
  [CultureTypeEnum.Soybean]: "Soja",
  [CultureTypeEnum.Demo]: "Demonstração",
  [CultureTypeEnum.Soil]: "Solo",
  [CultureTypeEnum.Eucalyptus]: "Eucalipto",
  [CultureTypeEnum.Other]: "Outras",
  [CultureTypeEnum.SugarcaneLeaf]: "Cana-de-açúcar (Folha)"
};

/** Mapeamento de nomes de enum (string) para labels pt. */
const ENUM_NAME_TO_PT: Record<string, string> = {
  cotton: "Algodão",
  coffe: "Café",
  coffee: "Café",
  sugar: "Cana-de-açúcar",
  maize: "Milho",
  soybean: "Soja",
  demo: "Demonstração",
  soil: "Solo",
  eucalyptus: "Eucalipto",
  other: "Outras",
};

const LABELS_EN: Record<number, string> = {
  [CultureTypeEnum.Cotton]: "Cotton",
  [CultureTypeEnum.Coffe]: "Coffe",
  [CultureTypeEnum.SugarcaneStalk]: "SugarcaneStalk",
  [CultureTypeEnum.Maize]: "Maize",
  [CultureTypeEnum.Soybean]: "Soybean",
  [CultureTypeEnum.Demo]: "Demo",
  [CultureTypeEnum.Soil]: "Soil",
  [CultureTypeEnum.Eucalyptus]: "Eucalyptus",
  [CultureTypeEnum.Other]: "Other",
  [CultureTypeEnum.SugarcaneLeaf]: "SugarcaneLeaf"
};

const OTHER_CULTURES: string[] = [
  "Arroz", "Banana", "Batata", "Batata Doce", "Canola", "Café",
  "Cevada", "Citrus", "Feijão", "Laranja", "Maçã", 
  "Melancia", "Sorgo", "Tomate", "Trigo", "Uva"
];

/** Rótulo para UI (pt). */
export function cultureTypeLabelPt(
  culture: number | string | undefined | null,
  customCultureName?: string | null
): string {
  if (customCultureName && String(customCultureName).trim() !== "") {
    return normalizeCustomCultureName(customCultureName);
  }
  if (culture === undefined || culture === null) return "";
  // Suporta enum como número (0-7) ou como string ("Soybean", "Maize", etc.)
  if (typeof culture === "string") {
    return ENUM_NAME_TO_PT[culture.toLowerCase()] ?? culture;
  }
  const n = Math.floor(Number(culture));
  if (isNaN(n)) return String(culture);
  return LABELS_PT[n] ?? "";
}

function normalizeCustomCultureName (customCulture: string) {
  const culture = OTHER_CULTURES.find(
    c => normalizeText(c) === normalizeText(customCulture));

  if (culture) return culture;

  const cultureWithoutSpaces = customCulture.trim();
  const normalizedCultureName = 
    cultureWithoutSpaces.charAt(0).toUpperCase() + cultureWithoutSpaces.slice(1).toLowerCase();

  return normalizedCultureName;
}

function normalizeText (text: string) {
  return text
    .trim()
    .toLowerCase()
    .normalize("NFD")
    .replace(/\p{Diacritic}/gu, "")
}

/** Rótulo para export (inglês, alinhado aos nomes de enum). */
export function cultureTypeLabelExport(culture?: number | null): string {
  if (culture === undefined || culture === null) return "";
  const n = Math.floor(Number(culture));
  return LABELS_EN[n] ?? "";
}

export const PlantThirdLabelsPt: Record<number, string> = {
  0: "Superior",
  1: "Médio",
  2: "Inferior",
  3: "Todas",
  4: "Médio Superior"
};

/** Mapeamento de nomes de enum (string) para labels pt. */
const PLANT_THIRD_NAME_TO_PT: Record<string, string> = {
  upper: "Superior",
  middle: "Médio",
  lower: "Inferior",
  all: "Todas",
  uppermiddle: "Médio Superior"
};

export function plantThirdLabelPt(val: number | string | undefined | null): string {
  if (val === undefined || val === null) return "";
  if (typeof val === "string") {
    return PLANT_THIRD_NAME_TO_PT[val.toLowerCase()] ?? val;
  }
  const n = Number(val);
  if (isNaN(n)) return String(val);
  return PlantThirdLabelsPt[n] ?? String(val);
}
